import React, { useCallback, useEffect, useState, Suspense, lazy } from "react";
import { Switch, Route } from "react-router-dom";
import { useDispatch, useSelector, connect } from "react-redux";
import appConfig from "config.json";
import NotFound from "containers/Pages/Standalone/NotFoundDedicated";
import NotificationsProvider from "components/NotificationsProvider";
import ConfirmationProvider from "components/ConfirmationProvider/ConfirmationProvider";
import ForgotPasswordDedicated from "containers/Pages/Standalone/ForgotPasswordDedicated";
import PasswordRecoveryDedicated from "containers/Pages/Standalone/PasswordRecoveryDedicated";
import Test from "containers/Pages/Test/Test";
import LoginDedicated from "../Pages/Standalone/LoginDedicated";
import ThemeWrapper, { AppContext } from "./ThemeWrapper";
import ProtectedRoute from "../../utils/ProtectedRoute";
import Auth from "./Auth";
import Spinner from "../../components/Spinner/Spinner";
import { ADD_NOTIFICATION } from "../../actions/actionConstants";
// Crypto wallet disabled for HTTP deployment
import PrivyWrapper from "./PrivyWrapper";

// Lazy load heavy dashboard application - loads only after login
const Application = lazy(() => import("./Application"));

window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;

const App = ({ jwtToken }) => {
  const dispatch = useDispatch();
  const [appState, setAppState] = useState("loading");
  const loggedIn = useSelector((state) =>
    state.getIn(["users", "user", "loggedIn"])
  );

  useEffect(() => {
    /**
         * Fetches the config from the backend and sets it in the appConfig to be as a global state.
         * It also sets the appState to "loaded" when the config is fetched.
         * It also sets the appState to "done" when the config is fetched and initialized.
         * @returns {Promise<void>}
         */
    const handleGetConfig = async () => {
      try {
        dispatch({
          type: ADD_NOTIFICATION,
          message: "We are initializing our services.",
        });
        const response = await fetch(
          `${process.env.REACT_APP_RESTAPI}/frontend/setting?frontend_id=${appConfig.identifier}`,
          {
            method: "GET",
          }
        );

        const data = await response.json();
        if (response.ok) {
          appConfig.config = {
            ...appConfig.config,
            ...data,
            loading: false,
          };
        }
      } catch (error) {
        dispatch({
          type: ADD_NOTIFICATION,
          message:
            "An error occurred while initializing our services. Try to reload.",
        });
      } finally {
        setAppState("loaded");
      }
    };

    // Load userInfo from localStorage on app start
    try {
      const savedUserInfo = localStorage.getItem("userInfo");
      console.log("🚀 App startup - checking localStorage for userInfo:", savedUserInfo);
      if (savedUserInfo) {
        const userInfo = JSON.parse(savedUserInfo);
        console.log("🚀 App startup - parsed userInfo:", userInfo);
        console.log("🚀 App startup - userInfo.id:", userInfo?.id);
        console.log("🚀 App startup - userInfo.roles:", userInfo?.roles);
        dispatch({ type: "SAVE_USER_INFO", userInfo });
        console.log("✅ Loaded userInfo from localStorage in App:", userInfo);
      } else {
        console.log("🚀 App startup - no userInfo found in localStorage");
      }
    } catch (e) {
      console.warn("Could not load userInfo from localStorage in App:", e);
    }

    handleGetConfig();
  }, [dispatch]);

  useEffect(() => {
    if (appState === "loaded") {
      // Crypto wallet features disabled for HTTP deployment
      console.log("Crypto wallet features disabled - skipping wallet initialization");
      setAppState("done");
    }
  }, [appState]);

  return appState !== "done" ? (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        flexDirection: "column",
      }}
    >
      <Spinner />
    </div>
  ) : (
    <ThemeWrapper>
      {/* Crypto wallet disabled for HTTP deployment */}
      <PrivyWrapper>
        <NotificationsProvider />
        <ConfirmationProvider />
        <AppContext.Consumer>
          {(changeMode) => (
            <Switch>
              <Route
                path="/forgot-password"
                exact
                component={ForgotPasswordDedicated}
              />
              <Route path="/test" exact component={Test} />
              <Route
                path="/password-recovery"
                exact
                component={PasswordRecoveryDedicated}
              />
              <Route
                path="/login"
                exact
                component={LoginDedicated}
              />
              <ProtectedRoute
                path="/"
                render={(props) => (
                  <Suspense
                    fallback={(
                      <div
                        style={{
                          width: "100%",
                          height: "100vh",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "10px",
                          flexDirection: "column",
                        }}
                      >
                        <Spinner />
                        <div>Loading dashboard...</div>
                      </div>
                    )}
                  >
                    <Application
                      {...props}
                      changeMode={changeMode}
                    />
                  </Suspense>
                )}
                skipRoles
              />
              <Route component={Auth} />
              <Route component={NotFound} />
            </Switch>
          )}
        </AppContext.Consumer>
      </PrivyWrapper>
    </ThemeWrapper>
  );
};

export default connect((state) => ({
  jwtToken: state.getIn(["users", "user", "token"]),
}))(App);
